import React, {
  useRef,
  forwardRef,
  useImperativeHandle,
  useState,
} from 'react';
import { Box, Card, Divider, Typography, Tabs, Tab, Chip } from '@mui/material';
import { Person, CalendarToday } from '@mui/icons-material';
import dayjs from 'dayjs';
import DataEntryModal from '../../../../GoalData';
import TabPanel from './TabPanel';
import { formatDate } from '../utils';
import { getGoalDataForTab } from '../utils/goalDataHelpers';
import { handleGetEditingDataPoint } from '../utils';

import FullScreenGoalDetailsView from '../../../../FullScreenGoalDetailsView';
const GoalTabsSection = forwardRef(
  (
    {
      selectedGoals,
      selectedTabIndex,
      onTabChange,
      user,
      userName,
      formState,
      studentGoalsData,
      originalGoalIds,
      originalGoalData,
      registerModalData,
      dataEntryRef,
      draftEntries,
      onUpsertDraft,
      onRemoveDraft,
    },
    ref
  ) => {
    // Create refs for each goal modal
    const goalModalRefs = useRef({});

    const [fullScreenGoalModal, setFullScreenGoalModal] = useState({
      open: false,
      goal: null,
      goalData: null,
    });

    const handleOpenGoalDataEntryModal = (goal, allGoalData) => {
      setFullScreenGoalModal({
        open: true,
        goal,
        goalData: allGoalData || goal?.info || null,
      });
    };

    const handleCloseGoalDataEntryModal = () => {
      setFullScreenGoalModal({
        open: false,
        goal: null,
        goalData: null,
      });
    };


    const checkForExistingDataEntry = (
      validGoalData,
      currentDate,
      multipleDataPointsValue,
      subGoalIndex
    ) => {

      if (multipleDataPointsValue === 'Yes') {
        return null;
      }

      if (!validGoalData || !Array.isArray(validGoalData) || validGoalData.length === 0) {
        return null;
      }


      const existingDataPoint = validGoalData.find((item) => {
        if (!item || !item.createdAt) return false;

        let itemDate;
        if (item.createdAt._seconds) {
          itemDate = dayjs(new Date(item.createdAt._seconds * 1000)).format('YYYY-MM-DD');
        } else if (typeof item.createdAt === 'string') {
          itemDate = dayjs(item.createdAt).format('YYYY-MM-DD');
        } else if (item.createdAt instanceof Date) {
          itemDate = dayjs(item.createdAt).format('YYYY-MM-DD');
        } else {
          return false;
        }

        if (itemDate === currentDate) {

          if (item?.info?.data_points && item?.info?.data_points?.length > 0) {

            if (subGoalIndex !== null) {
              return item.info.data_points.some(
                (point) => point.currentGoal === subGoalIndex
              );
            }
            return true;
          }
        }
        return false;
      });

      return existingDataPoint || null;
    };


    useImperativeHandle(
      ref,
      () => ({
        saveAllGoals: async () => {
          const results = [];
          const errors = [];

          for (const goal of selectedGoals) {
            const modalRef = goalModalRefs.current[goal.id];
            if (modalRef && typeof modalRef.save === 'function') {
              try {
                const result = await modalRef.save();
                results.push(result);
              } catch (error) {
                errors.push({ goalId: goal.id, error });
              }
            }
          }

          if (errors.length > 0) {
            throw new Error(`Failed to save ${errors.length} goal(s)`);
          }

          return results;
        },
      }),
      [selectedGoals]
    );

    if (selectedGoals.length === 0) return null;

    return (
      <Box sx={{ px: 3, pb: 3 }}>
        <Divider sx={{ mb: 3 }} />


        <Typography
          variant='subtitle1'
          sx={{
            fontFamily: 'Lato, sans-serif',
            fontWeight: 600,
            color: '#2c3e50',
            mb: 2,
          }}
        >
          Selected Goals for Data Entry
        </Typography>


        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            border: '2px solid #e9ecef',
          }}
        >
          <Box
            sx={{
              borderBottom: '1px solid #e9ecef',
              backgroundColor: '#f8f9ff',
            }}
          >
            <Tabs
              value={selectedTabIndex}
              onChange={onTabChange}
              variant='scrollable'
              scrollButtons='auto'
              sx={{
                '& .MuiTab-root': {
                  fontFamily: 'Lato, sans-serif',
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  color: '#666',
                  '&.Mui-selected': {
                    color: '#9291ef',
                    fontWeight: 600,
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#9291ef',
                },
              }}
            >
              {selectedGoals.map((goal, index) => (
                <Tab
                  key={goal.id}
                  label={goal.title}
                  id={`goal-tab-${index}`}
                  aria-controls={`goal-tabpanel-${index}`}
                />
              ))}
            </Tabs>
          </Box>

          {selectedGoals.map((goal, index) => {
            const currentDate = formState?.date || formatDate(new Date());
            const entryKey = `${goal.id}-${currentDate}`;

            const {
              fullGoalData,
              validGoalData,
              studentDataPoints,
              criteriaValue,
              promptingValue,
              multipleDataPointsValue,
              subGoalIndex,
              targetValue,
            } = getGoalDataForTab(goal, user, formState, studentGoalsData);

            const isOriginalGoal = originalGoalIds?.includes(goal.id);
            const editingDataPointFromOriginal = isOriginalGoal
              ? handleGetEditingDataPoint(
                originalGoalData?.find((g) => g.goalID === goal.id),
                user,
                formState,
                studentGoalsData
              )
              : null;


            const existingDataEntry = checkForExistingDataEntry(
              validGoalData,
              currentDate,
              multipleDataPointsValue,
              subGoalIndex
            );


            const editingDataPoint = editingDataPointFromOriginal || existingDataEntry;


            const shouldShowWarning =
              !!existingDataEntry && !editingDataPointFromOriginal && multipleDataPointsValue === 'No';

            const commonDataEntryProps = {
              open: true,
              onClose: () => { },
              goalId: goal.id,
              goalTitle: goal.title,
              goalNumber: fullGoalData?.info?.GoalDetails?.GoalNumber || null,
              goalData: validGoalData,
              goalDate: currentDate,
              selectedDate: currentDate,
              studentId: user?.studentId || user?.id,
              studentDataPoints,
              selectedGoalIds: selectedGoals,
              selectedStudents: {
                id: user?.studentId || user?.id,
                name: userName || user?.name || user?.fullname,
              },
              MultipleDataPointsPerSession: multipleDataPointsValue,
              measurementDuration:
                fullGoalData?.info?.GoalDetails?.measurementDuration || null,
              measurementUnit:
                fullGoalData?.info?.GoalDetails?.measurementUnit || null,
              dataPointPerSession:
                fullGoalData?.info?.GoalDetails?.dataPointPerSession || null,
              target: targetValue,
              goalType:
                fullGoalData?.info?.DataCollection?.measurementType?.Type ||
                'N/A',
              subGoalIndex,
              criteria: criteriaValue,
              prompting: promptingValue,
              trialsMax: fullGoalData?.info?.GoalDetails?.trialsMax || 'N/A',
              allGoalData: fullGoalData?.info || null,
              warning: shouldShowWarning,
              warningKeepOnlyOne: false,
              warningFromVideo: false,
              onSuccess: () => { },
              registerModalData,
              entryKey,
              fromSessionNotes: true,
              dataEntryRef,
              draftState: draftEntries[goal.id],
              onDraftChange: (draft) => {
                if (typeof onUpsertDraft === 'function') {
                  onUpsertDraft(goal.id, draft);
                }
              },
              onDraftClear: () => {
                if (typeof onRemoveDraft === 'function') {
                  onRemoveDraft(goal.id);
                }
              },
            };

            return (
              <TabPanel key={goal.id} value={selectedTabIndex} index={index}>
                <Box sx={{ p: 3 }}>
                  <Box
                    sx={{
                      p: 3,
                      bgcolor:
                        'linear-gradient(135deg, #f8f9ff 0%, #f0f1ff 100%)',
                      borderRadius: 2,
                      mb: 2,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Chip
                        icon={<Person sx={{ fontSize: 18 }} />}
                        label={userName || user?.name || user?.fullname}
                        variant='outlined'
                        sx={{
                          fontWeight: 600,
                          bgcolor: 'white',
                          border: '1px solid #9291ef',
                          color: '#9291ef',
                          fontSize: '0.9rem',
                          height: 36,
                          '& .MuiChip-icon': {
                            color: '#9291ef',
                          },
                        }}
                      />
                    </Box>
                    <Box>
                      <Chip
                        label={'View Goal Details'}
                        onClick={() => {
                          handleOpenGoalDataEntryModal(
                            goal,
                            fullGoalData?.info
                          );
                        }}
                        variant='filled'
                        sx={{
                          fontWeight: 600,
                          bgcolor: '#9291ef',
                          color: 'white',
                          fontSize: '0.9rem',
                          height: 36,
                          marginRight: 1,
                        }}
                      />
                      <Chip
                        icon={<CalendarToday sx={{ fontSize: 18 }} />}
                        label={currentDate}
                        variant='outlined'
                        sx={{
                          fontWeight: 600,
                          bgcolor: 'white',
                          border: '1px solid #9291ef',
                          color: '#9291ef',
                          fontSize: '0.9rem',
                          height: 36,
                          '& .MuiChip-icon': {
                            color: '#9291ef',
                          },
                        }}
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      borderRadius: 2,
                      border: '1px solid #e9ecef',
                      overflow: 'hidden',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    }}
                  >
                    <DataEntryModal
                      ref={(el) => {
                        if (el) {
                          goalModalRefs.current[goal.id] = el;
                        }
                      }}
                      {...commonDataEntryProps}
                      editingDataPoint={editingDataPoint}
                    />
                  </Box>
                </Box>
              </TabPanel>
            );
          })}
        </Card>
        <FullScreenGoalDetailsView
          open={fullScreenGoalModal.open}
          onClose={handleCloseGoalDataEntryModal}
          goalData={fullScreenGoalModal.goalData}
          goals={fullScreenGoalModal.goal}
          fromSessionNotes={true}
        />
      </Box>
    );
  }
);

export default GoalTabsSection;
